(() => {
  'use strict';
  const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221%22 height=%221%22/%3E';

  class LatestRequest {
    start() {
      this.controller?.abort();
      this.controller = new AbortController();
      const version = this.version = (this.version || 0) + 1;
      return { signal: this.controller.signal, current: () => this.version === version && !this.controller.signal.aborted };
    }
    cancel() { this.controller?.abort(); this.version = (this.version || 0) + 1; }
  }
  function nearby(index, length) {
    return Array.from({ length }, (_, i) => i).filter(i => Math.abs(i - index) <= 1);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { LatestRequest, nearby };
  if (typeof document === 'undefined') return;

  // The native slider, media openers and variant form remain the integration API.
  class ProductMediaBudget {
    constructor(section) {
      this.section = section;
      this.desktop = matchMedia('(min-width: 990px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.queue = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      };
      this.observer = new MutationObserver(this.queue);
      section.querySelectorAll('swiper-slider, theme-lightbox').forEach(element => {
        this.observer.observe(element, { subtree: true, attributes: true, attributeFilter: ['class'] });
      });
      section.addEventListener('slide_inited', this.queue, { ...options, capture: true });
      section.addEventListener('slide_changed_custom', this.queue, { ...options, capture: true });
      window.addEventListener('resize', this.queue, { ...options, passive: true });
      section.addEventListener('click', event => {
        const opener = event.target.closest('lightbox-opener');
        if (!opener) return;
        const lightbox = document.getElementById(opener.dataset.id);
        const slides = this.slides(lightbox?.querySelector('swiper-slider'));
        const index = slides.findIndex(slide => slide.querySelector('[data-media-id]')?.dataset.mediaId === opener.dataset.mediaId);
        if (index >= 0) this.apply(slides, index, true);
      }, { ...options, capture: true });
      section.querySelectorAll('[data-neko-zoom-image]').forEach(image => {
        image.addEventListener('load', () => {
          const lightbox = image.closest('theme-lightbox');
          if (lightbox?.classList.contains('active')) lightbox.resizeHandler?.();
        }, options);
      });
      this.sync();
    }
    slides(slider) { return slider ? [...slider.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.hide)')] : []; }
    setImage(image, enabled) {
      const source = image.parentElement.querySelector('source[data-neko-srcset]');
      if (source) {
        if (enabled && source.getAttribute('srcset') !== source.dataset.nekoSrcset) source.setAttribute('srcset', source.dataset.nekoSrcset);
        if (!enabled && source.hasAttribute('srcset')) source.removeAttribute('srcset');
      }
      // A picture's source applies only at its own viewport breakpoint. Its
      // fallback stays tiny, so hidden desktop and mobile galleries do not load.
      const src = enabled && !source ? image.dataset.nekoSrc : PLACEHOLDER;
      if (image.getAttribute('src') !== src) image.setAttribute('src', src);
      if (enabled) image.setAttribute('loading', 'eager');
    }
    apply(slides, index, enabled) {
      const wanted = enabled ? nearby(index, slides.length) : [];
      slides.forEach((slide, i) => {
        slide.querySelectorAll('[data-neko-gallery-image], [data-neko-zoom-image]').forEach(image => this.setImage(image, wanted.includes(i)));
      });
    }
    sync() {
      for (const [selector, visible] of [['.product--thumbnail_slider', this.desktop.matches], ['.product--thumbnail_slider__mobile', !this.desktop.matches]]) {
        const gallery = this.section.querySelector(selector);
        const slides = this.slides(gallery?.querySelector('swiper-slider'));
        const index = Math.max(0, slides.findIndex(slide => slide.classList.contains('swiper-slide-active')));
        this.apply(slides, index, visible);
        gallery?.querySelectorAll('.hide [data-neko-gallery-image]').forEach(image => this.setImage(image, false));
      }
      const lightbox = this.section.querySelector('theme-lightbox');
      const slides = this.slides(lightbox?.querySelector('swiper-slider'));
      const index = Math.max(0, slides.findIndex(slide => slide.classList.contains('swiper-slide-active')));
      this.apply(slides, index, Boolean(lightbox?.classList.contains('active')));
    }
    destroy() { this.abort.abort(); this.observer.disconnect(); cancelAnimationFrame(this.frame); }
  }

  class ProductVariantController {
    constructor(section, media) {
      this.section = section;
      this.media = media;
      this.control = section.querySelector('variant-selects');
      this.requests = new LatestRequest();
      this.abort = new AbortController();
      if (!this.control) return;
      this.control.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"], select')) return;
        event.stopImmediatePropagation();
        const option = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
        this.change(option?.dataset.variantId);
      }, { capture: true, signal: this.abort.signal });
      this.section.addEventListener('submit', event => {
        if (this.control.getAttribute('aria-busy') === 'true' || this.failed) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.abort.signal });
      this.section.addEventListener('click', event => {
        if ((this.control.getAttribute('aria-busy') === 'true' || this.failed) && event.target.closest('product-form')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.abort.signal });
      this.section.addEventListener('slide_inited', () => {
        if (this.control.currentVariant) this.updateMedia(this.control.currentVariant);
      }, { signal: this.abort.signal, capture: true });
      window.addEventListener('resize', () => {
        const desktop = matchMedia('(min-width: 990px)').matches;
        if (this.desktop !== desktop) {
          this.desktop = desktop;
          if (this.control.currentVariant) this.updateMedia(this.control.currentVariant);
        }
      }, { passive: true, signal: this.abort.signal });
      this.desktop = matchMedia('(min-width: 990px)').matches;
      this.includedGift();
    }
    includedGift() {
      this.control.querySelectorAll('.product-form__input').forEach(field => {
        const radios = [...field.querySelectorAll('input[type="radio"]')];
        const select = field.querySelector('select');
        const name = radios[0]?.name || select?.name || '';
        const choices = radios.length || [...(select?.options || [])].filter(option => option.value).length;
        if (!/^(regalo|gift)$/i.test(name.trim()) || choices !== 1) return;
        if (radios[0]) radios[0].checked = true;
        field.classList.add('neko-included-option');
        const legend = field.querySelector('.variants-label');
        const value = radios[0]?.value || select?.value;
        if (legend && value) {
          const included = this.section.dataset.nekoIncluded || 'Incluye:';
          if (legend.textContent.trim() !== included + ' ' + value) {
            legend.replaceChildren(document.createTextNode(included + ' '));
            const label = document.createElement('span');
            label.className = 'variants-label__value';
            label.textContent = value;
            legend.append(label);
          }
        }
      });
    }
    addButton() { return document.getElementById('product-form-' + this.control.dataset.section)?.querySelector('[name="add"]'); }
    busy(value) {
      this.control.setAttribute('aria-busy', String(value));
      const form = document.getElementById('product-form-' + this.control.dataset.section);
      const locked = value || this.failed;
      if (form) {
        form.setAttribute('aria-busy', String(value));
        if (locked && !this.lockedForm) {
          this.lockedForm = form;
          this.formWasInert = form.hasAttribute('inert');
          form.setAttribute('inert', '');
        } else if (!locked && this.lockedForm) {
          if (!this.formWasInert) this.lockedForm.removeAttribute('inert');
          this.lockedForm = null;
        }
      }
      const add = this.addButton();
      if (add) {
        add.setAttribute('aria-busy', String(value));
        if (value) add.disabled = true;
      }
    }
    clearError() { this.error?.remove(); this.error = null; this.failed = false; }
    showError(id) {
      this.failed = true;
      this.error?.remove();
      this.error = document.createElement('div');
      this.error.className = 'neko-variant-error';
      this.error.setAttribute('role', 'alert');
      this.error.textContent = this.section.dataset.nekoVariantError || 'No se pudo actualizar la opción. Vuelve a elegir el color.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = this.section.dataset.nekoRetry || 'Reintentar';
      retry.addEventListener('click', () => this.change(id), { once: true });
      this.error.append(retry);
      this.control.after(this.error);
      this.addButton()?.setAttribute('disabled', '');
    }
    async change(id) {
      const request = this.requests.start();
      this.clearError();
      if (!id) {
        this.busy(false);
        this.control.toggleAddButton(true);
        this.control.setUnavailable();
        return;
      }
      this.busy(true);
      const url = new URL(this.control.dataset.url, location.origin);
      url.searchParams.set('variant', id);
      url.searchParams.set('section_id', this.control.dataset.originalSection || this.control.dataset.section);
      url.searchParams.set('view', 'neko-product');
      try {
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Variant request failed');
        const text = await response.text();
        if (!request.current() || !this.control.isConnected) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const json = html.querySelector('variant-selects script[type="application/json"]')?.textContent;
        const variant = JSON.parse(json || 'null');
        const form = html.getElementById('product-form-' + this.control.dataset.section);
        if (String(variant?.id) !== String(id) || !form?.querySelector('[name="add"]')) throw new Error('Incomplete variant response');
        const control = this.control;
        control.variant_id = String(id);
        control.new_html = html;
        control.newJSON = json;
        control.querySelector('script[type="application/json"]').textContent = json;
        control.updateMasterId();
        control.updateOptions();
        control.updatePickupAvailability();
        control.removeErrorMessage();
        control.updateVariantStatuses();
        this.updateMedia(variant);
        // Preserve preview, template view and attribution parameters without navigation.
        const current = new URL(location.href);
        current.searchParams.set('variant', id);
        history.replaceState(history.state, '', current);
        control.updateVariantInput();
        control.renderProductInfo();
        control.updateShareUrl();
        this.includedGift();
        this.media.sync();
      } catch (error) {
        if (request.current() && error.name !== 'AbortError') this.showError(id);
      } finally {
        if (request.current()) this.busy(false);
      }
    }
    updateMedia(variant) {
      const color = String(variant.option1 || '').toLowerCase();
      this.section.querySelectorAll('[data-main-color]').forEach(group => {
        group.dataset.mainColor = color;
        const items = [...group.querySelectorAll('[data-current-color]')];
        if (!items.some(item => item.dataset.currentColor === color)) return;
        items.forEach(item => {
          const selected = item.dataset.currentColor === color;
          item.classList.toggle('hide', !selected);
          if (item.closest('swiper-slider')) item.classList.toggle('swiper-slide', selected);
          item.classList.remove('color-first-item');
        });
        items.find(item => item.dataset.currentColor === color)?.classList.add('color-first-item');
      });
      this.section.querySelectorAll('swiper-slider-container').forEach(container => {
        const slider = container.querySelector('swiper-slider');
        const visible = slider?.getBoundingClientRect().width > 0 && slider?.getBoundingClientRect().height > 0;
        const id = variant.featured_media?.id;
        if (id) container.dataset.currentMediaId = id;
        if (!visible || !slider.swiper || typeof slider.swiper.update !== 'function') return;
        const slides = this.media.slides(slider);
        const index = id ? slides.findIndex(slide => [...slide.querySelectorAll('[data-media-id]')].some(item => String(item.dataset.mediaId) === String(id))) : 0;
        slider.swiper.update();
        slider.swiper.slideTo(Math.max(0, index), 0);
        slider.initPaginationCss?.();
        if (id && container.querySelector('.swiper-thumb[data-media-id="' + id + '"]')) {
          if (container.classList.contains('swiper-thumb__vertical')) container.verticaldesign?.(id);
          else container.horizontaldesign?.(id);
        }
      });
      this.media.sync();
    }
    destroy() { this.requests.cancel(); this.abort.abort(); this.clearError(); if (this.control) this.busy(false); }
  }

  const controllers = new WeakMap();
  function setup(root = document) {
    root.querySelectorAll('.neko-product').forEach(section => {
      if (controllers.has(section)) return;
      const media = new ProductMediaBudget(section);
      const controller = new ProductVariantController(section, media);
      controllers.set(section, { media, controller });
    });
  }
  customElements.whenDefined('variant-selects').then(() => setup());
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.neko-product').forEach(section => {
      const instances = controllers.get(section);
      instances?.controller.destroy();
      instances?.media.destroy();
      controllers.delete(section);
    });
  });
})();
